import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  FileText,
  AlertTriangle,
  Calendar,
  Sparkles,
  Menu,
  X,
  LogOut,
  CreditCard,
  Phone,
  Smartphone,
  Receipt,
  UserCog,
  Search,
  ChevronDown,
  MessageSquare,
  Building2,
  Home,
  Mail
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, hasModuleAccess, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    insurance: true,
    telecom: true
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const navSections = [
    {
      id: 'general',
      title: 'Generale',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      module: 'general',
      items: [
        { path: '/clients', label: 'Anagrafica Clienti', icon: Users },
        { path: '/delega', label: 'Delega', icon: FileText },
        ...(user?.role === 'superadmin' ? [{ path: '/company-profile', label: 'Profilo Ditta', icon: Building2 }] : [])
      ]
    },
    {
      id: 'insurance',
      title: 'Assicurazioni',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      module: 'insurance',
      items: [
        { path: '/quotes', label: 'Preventivi', icon: FileText },
        { path: '/policies', label: 'Polizze', icon: FileText },
        { path: '/claims', label: 'Sinistri', icon: AlertTriangle },
        { path: '/renewals', label: 'Scadenze', icon: Calendar },
        { path: '/payments', label: 'Pagamenti', icon: CreditCard }
      ]
    },
    {
      id: 'consulting',
      title: 'Consulenza',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      module: 'insurance',
      items: [
        { path: '/consulting', label: 'Consulenze', icon: MessageSquare }
      ]
    },
    {
      id: 'services',
      title: 'Servizi',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      module: 'insurance',
      items: [
        { path: '/canone-rai', label: 'Canone RAI', icon: FileText },
        { path: '/mutui', label: 'Mutui/Prestiti', icon: Home }
      ]
    },
    {
      id: 'telecom',
      title: 'Telefonia',
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      module: 'telecom',
      items: [
        { path: '/telecom/contracts', label: 'Contratti', icon: Phone },
        { path: '/telecom/billing', label: 'Fatturazione', icon: Receipt }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <img 
              src="/jv-service-logo.png" 
              alt="JV Service" 
              className="h-10 w-auto object-contain"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-mobile">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-slate-900 text-white text-sm">{getInitials(user?.full_name)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="flex items-center gap-2">
                <span>{user?.full_name}</span>
                {isAdmin() && <Badge className="bg-amber-100 text-amber-700 text-xs">Admin</Badge>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 flex items-center gap-2">
                <LogOut className="w-4 h-4" /><span>Esci</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
        <div className="flex flex-col h-full">
          {/* Logo - Sezione ingrandita */}
          <div className="flex items-center justify-start px-4 py-6 border-b border-slate-100" style={{ minHeight: '140px' }}>
            <img 
              src="/jv-service-logo.png" 
              alt="JV Service" 
              className="w-full h-auto object-contain"
              style={{ maxHeight: '120px', maxWidth: '220px' }}
            />
          </div>

          {/* Search - Con margine superiore */}
          <div className="p-4 pt-5 border-b border-slate-100">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 text-sm"
                  data-testid="global-search"
                />
              </div>
            </form>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {/* Dashboard */}
            {isAdmin() && (
              <Link
                to="/dashboard"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                  location.pathname === '/dashboard' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="nav-dashboard"
              >
                <LayoutDashboard className="w-5 h-5" />
                <span>Dashboard</span>
              </Link>
            )}

            {/* Module Sections */}
            {navSections.map((section) => (
              hasModuleAccess(section.module) && (
                <div key={section.id} className="space-y-1">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className={`flex items-center justify-between w-full px-4 py-2 text-sm font-semibold ${section.color} rounded-lg ${section.bgColor}`}
                  >
                    <span>{section.title}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections[section.id] ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedSections[section.id] && (
                    <div className="ml-2 space-y-1">
                      {section.items.map((item) => {
                        const isActive = location.pathname === item.path;
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                              isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                            }`}
                            data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )
            ))}

            {/* AI Assistant */}
            <Link
              to="/ai-assistant"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                location.pathname === '/ai-assistant' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="nav-ai-assistant"
            >
              <Sparkles className="w-5 h-5" />
              <span>AI Assistant</span>
            </Link>

            {/* Admin: User Management */}
            {isAdmin() && (
              <Link
                to="/users"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                  location.pathname === '/users' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="nav-users"
              >
                <UserCog className="w-5 h-5" />
                <span>Gestione Utenti</span>
              </Link>
            )}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-slate-100 transition-colors" data-testid="user-menu-desktop">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-slate-900 text-white">{getInitials(user?.full_name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-slate-900 text-sm truncate">{user?.full_name}</p>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                      {user?.email}
                      {isAdmin() && <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-1">Admin</Badge>}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 flex items-center gap-2">
                  <LogOut className="w-4 h-4" /><span>Esci</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
